import type {
  CalendarView,
  MyWork,
  MyWorkBadge,
  Page,
  TaskActivity,
  TaskActivityItem,
  TaskAssignInput,
  TaskBlockInput,
  TaskBoard,
  TaskBoardQuery,
  TaskBulkInput,
  TaskBulkResult,
  TaskCalendar,
  TaskCalendarQuery,
  TaskCreateInput,
  TaskDateRequestInput,
  TaskDetail,
  TaskLinkInput,
  TaskListItem,
  TaskListQuery,
  TaskMoveInput,
  TaskPerson,
  TaskPersonRole,
  TaskRelated,
  TaskRelatedQuery,
  TaskUnblockInput,
  TaskUpdateInput,
  TaskViewCounts,
  Team,
} from '@sahab/contracts'
import {
  keepPreviousData,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { api, type DownloadedFile, type QueryValue } from '../../lib/api'
import type { Language } from '../../lib/format'

export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (query: TaskListQuery) => [...taskKeys.lists(), query] as const,
  counts: (query: TaskListQuery) => [...taskKeys.all, 'counts', query] as const,
  board: (query: TaskBoardQuery) => [...taskKeys.all, 'board', query] as const,
  calendar: (query: TaskCalendarQuery) => [...taskKeys.all, 'calendar', query] as const,
  myWork: () => [...taskKeys.all, 'my-work'] as const,
  badge: () => [...taskKeys.all, 'badge'] as const,
  related: (query: TaskRelatedQuery) => [...taskKeys.all, 'related', query] as const,
  detail: (id: string) => [...taskKeys.all, 'detail', id] as const,
  activity: (id: string, filter: string, page: number) =>
    [...taskKeys.all, 'activity', id, filter, page] as const,
  people: (q: string, teamId: string | undefined) => ['task-people', q, teamId ?? ''] as const,
  teams: () => ['teams'] as const,
}

/** Drops undefined keys, so the query string and the cache key stay the same for equal filters. */
function params(query: object): Record<string, QueryValue> {
  return Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== '')) as Record<
    string,
    QueryValue
  >
}

export function taskListQueryOptions(query: TaskListQuery) {
  return queryOptions({
    queryKey: taskKeys.list(query),
    queryFn: ({ signal }) => api.get<Page<TaskListItem>>('/tasks', { signal, query: params(query) }),
    placeholderData: keepPreviousData,
  })
}

/** The register's tab counts for the same filters (the view itself left out). */
export function taskCountsQueryOptions(query: TaskListQuery) {
  const { view: _view, page: _page, pageSize: _size, sort: _sort, ...filters } = query
  return queryOptions({
    queryKey: taskKeys.counts(filters),
    queryFn: ({ signal }) => api.get<TaskViewCounts>('/tasks/counts', { signal, query: params(filters) }),
    placeholderData: keepPreviousData,
  })
}

export function taskBoardQueryOptions(query: TaskBoardQuery) {
  return queryOptions({
    queryKey: taskKeys.board(query),
    queryFn: ({ signal }) => api.get<TaskBoard>('/tasks/board', { signal, query: params(query) }),
    placeholderData: keepPreviousData,
  })
}

/** The calendar's period: tasks due (or starting) in it, and the unscheduled open work. */
export function taskCalendarQueryOptions(query: TaskCalendarQuery) {
  return queryOptions({
    queryKey: taskKeys.calendar(query),
    queryFn: ({ signal }) => api.get<TaskCalendar>('/tasks/calendar', { signal, query: params(query) }),
    placeholderData: keepPreviousData,
  })
}

/**
 * The company's working week and holidays over a calendar period (GET /calendar, open to anyone signed
 * in): the task calendar shades days off and names holidays. Under the 'calendar' key, so a holiday change
 * in settings reads it again.
 */
export function workingDaysQueryOptions(from: string, to: string) {
  return queryOptions({
    queryKey: ['calendar', 'view', from, to] as const,
    queryFn: ({ signal }) => api.get<CalendarView>('/calendar', { signal, query: { from, to } }),
    staleTime: 5 * 60_000,
  })
}

export const myWorkQueryOptions = queryOptions({
  queryKey: taskKeys.myWork(),
  queryFn: ({ signal }) => api.get<MyWork>('/tasks/my-work', { signal }),
})

/** The navigation badge: polled while the window is visible, like the approvals count. */
export const myWorkBadgeQueryOptions = queryOptions({
  queryKey: taskKeys.badge(),
  queryFn: ({ signal }) => api.get<MyWorkBadge>('/tasks/my-work/count', { signal }),
  refetchInterval: 60_000,
  refetchIntervalInBackground: false,
  staleTime: 30_000,
})

export function relatedTasksQueryOptions(query: TaskRelatedQuery) {
  return queryOptions({
    queryKey: taskKeys.related(query),
    queryFn: ({ signal }) => api.get<TaskRelated>('/tasks/related', { signal, query: params(query) }),
  })
}

export function taskQueryOptions(id: string) {
  return queryOptions({
    queryKey: taskKeys.detail(id),
    queryFn: ({ signal }) => api.get<TaskDetail>(`/tasks/${id}`, { signal }),
  })
}

export function taskActivityQueryOptions(id: string, filter: 'all' | 'comments' | 'history', page = 1) {
  return queryOptions({
    queryKey: taskKeys.activity(id, filter, page),
    queryFn: ({ signal }) =>
      api.get<TaskActivity>(`/tasks/${id}/activity`, { signal, query: { filter, page, pageSize: 100 } }),
    placeholderData: keepPreviousData,
  })
}

export function taskPeopleQueryOptions(q: string, teamId?: string) {
  return queryOptions({
    queryKey: taskKeys.people(q.trim(), teamId),
    queryFn: ({ signal }) =>
      api.get<TaskPerson[]>('/tasks/people', {
        signal,
        query: { q: q.trim() || undefined, teamId, limit: 20 },
      }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

/** People who can join a team (the team form, for teams.manage holders: works with Tasks off). */
export function teamPeopleQueryOptions(q: string, teamId?: string) {
  return queryOptions({
    queryKey: ['team-people', q.trim(), teamId ?? ''] as const,
    queryFn: ({ signal }) =>
      api.get<TaskPerson[]>('/teams/people', {
        signal,
        query: { q: q.trim() || undefined, teamId, limit: 20 },
      }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

/** One person by id, whatever their status (a filter chip naming who it filters by). */
export function taskPersonQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['task-people', 'one', id] as const,
    queryFn: async ({ signal }) =>
      (await api.get<TaskPerson[]>('/tasks/people', { signal, query: { id, limit: 1 } }))[0] ?? null,
    staleTime: 60_000,
  })
}

/** Active teams, for pickers and filters (one page is plenty: teams are few). */
export const taskTeamsQueryOptions = queryOptions({
  queryKey: taskKeys.teams(),
  queryFn: ({ signal }) => api.get<Page<Team>>('/teams', { signal, query: { pageSize: 200 } }),
  staleTime: 60_000,
})

/** One team with its members and counts (the team workspace, the breadcrumb). */
export function teamQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['teams', 'detail', id] as const,
    queryFn: ({ signal }) => api.get<Team>(`/teams/${id}`, { signal }),
  })
}

export function exportTasks(query: TaskListQuery, language: Language): Promise<DownloadedFile> {
  const { page: _page, pageSize: _size, ...filters } = query
  return api.download('/tasks/export', {
    query: { ...params(filters), lang: language },
    fallbackName: 'tasks.csv',
  })
}

/**
 * After any change to a task: its detail takes the answer, and every list, count, board, My work and
 * related panel refreshes (a move can change all of them).
 */
export function settleTask(client: QueryClient, task: TaskDetail | null, id?: string): void {
  if (task) client.setQueryData(taskKeys.detail(task.id), task)
  for (const key of ['list', 'counts', 'board', 'calendar', 'my-work', 'badge', 'related'] as const) {
    void client.invalidateQueries({ queryKey: [...taskKeys.all, key] })
  }
  void client.invalidateQueries({ queryKey: [...taskKeys.all, 'activity', task?.id ?? id] })
  void client.invalidateQueries({ queryKey: ['teams'] })
  void client.invalidateQueries({ queryKey: ['notifications'] })
}

function useTaskMutation<V>(run: (vars: V) => Promise<TaskDetail>) {
  const client = useQueryClient()
  return useMutation({ mutationFn: run, onSuccess: (task) => settleTask(client, task) })
}

export function useCreateTask() {
  return useTaskMutation((input: TaskCreateInput) => api.post<TaskDetail>('/tasks', input))
}

export function useUpdateTask(id: string) {
  return useTaskMutation((input: TaskUpdateInput) => api.patch<TaskDetail>(`/tasks/${id}`, input))
}

export function useAssignTask(id: string) {
  return useTaskMutation((input: TaskAssignInput) => api.post<TaskDetail>(`/tasks/${id}/assign`, input))
}

export function useClaimTask() {
  return useTaskMutation(({ id, version }: { id: string; version: number }) =>
    api.post<TaskDetail>(`/tasks/${id}/claim`, { version }),
  )
}

/** The one way status changes: buttons, the board and the Move to… menu all call it. */
export function useMoveTask() {
  return useTaskMutation(({ id, ...input }: TaskMoveInput & { id: string }) =>
    api.post<TaskDetail>(`/tasks/${id}/move`, input),
  )
}

export function useBlockTask(id: string) {
  return useTaskMutation((input: TaskBlockInput) => api.post<TaskDetail>(`/tasks/${id}/block`, input))
}

export function useUnblockTask(id: string) {
  return useTaskMutation((input: TaskUnblockInput) => api.post<TaskDetail>(`/tasks/${id}/unblock`, input))
}

export function useRequestDate(id: string) {
  return useTaskMutation((input: TaskDateRequestInput) =>
    api.post<TaskDetail>(`/tasks/${id}/date-requests`, input),
  )
}

export function useAddChecklistItem(id: string) {
  return useTaskMutation((text: string) => api.post<TaskDetail>(`/tasks/${id}/checklist`, { text }))
}

export function useUpdateChecklistItem(id: string) {
  return useTaskMutation(({ itemId, ...patch }: { itemId: string; text?: string; done?: boolean }) =>
    api.patch<TaskDetail>(`/tasks/${id}/checklist/${itemId}`, patch),
  )
}

export function useRemoveChecklistItem(id: string) {
  return useTaskMutation((itemId: string) => api.delete<TaskDetail>(`/tasks/${id}/checklist/${itemId}`))
}

export function useAddTaskPerson(id: string) {
  return useTaskMutation((input: { userId: string; role: TaskPersonRole }) =>
    api.post<TaskDetail>(`/tasks/${id}/people`, input),
  )
}

/** Null when the viewer removed themselves and may no longer see the task. */
export function useRemoveTaskPerson(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api.delete<{ task: TaskDetail | null }>(`/tasks/${id}/people/${userId}`),
    onSuccess: ({ task }) => {
      if (!task) client.removeQueries({ queryKey: taskKeys.detail(id) })
      settleTask(client, task, id)
    },
  })
}

export function useFollowTask(id: string) {
  return useTaskMutation(() => api.post<TaskDetail>(`/tasks/${id}/follow`))
}

export function useAddTaskLink(id: string) {
  return useTaskMutation((input: TaskLinkInput) => api.post<TaskDetail>(`/tasks/${id}/links`, input))
}

export function useRemoveTaskLink(id: string) {
  return useTaskMutation(({ entityType, entityId }: TaskLinkInput) =>
    api.delete<TaskDetail>(`/tasks/${id}/links/${entityType}/${entityId}`),
  )
}

export function useCommentTask(id: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: string) => api.post<TaskActivityItem>(`/tasks/${id}/comments`, { body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: [...taskKeys.all, 'activity', id] })
      void client.invalidateQueries({ queryKey: [...taskKeys.all, 'list'] })
    },
  })
}

export function useBulkTasks() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: TaskBulkInput) => api.post<TaskBulkResult>('/tasks/bulk', input),
    onSuccess: () => settleTask(client, null),
  })
}
